/* eslint-disable consistent-return */
/**
 *
 * Client Exclusive Card
 *
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { setLeavingModal } from 'containers/Main/actions';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import Card from 'components/Card';
import { getImageFile, isIE, toSentenceCase } from 'utils/stringUtils';
import { getAccentColor } from 'containers/Main/utils';
import Box from '@material-ui/core/Box';
import { useHistory } from 'react-router-dom';
import Mixpanel, { injectAssessmentResultsABData } from 'utils/mixpanelService';
import AssessmentResultContext from 'containers/AssessmentResult/AssessmentResultContext';
import { getMatchedLabels } from 'components/ResourceItemSmall/utils';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import SurveyResultContext from 'components/SurveyResult/SurveyResultContext';
import {
  getEventPageName,
  getThemesTitles,
  getTopicsTitles,
} from 'components/ClientResources/utils';
import LinkDetailCard from 'components/LinkItemSmall/LinkDetailCard';
import { getAlgoliaLocalizedField } from 'utils/localeUtils';
import { analyzeClientResource } from 'utils/analyzeClientResource';
import HighlightEntry from './HighlightEntry';
import DisclaimerEntry from './DisclaimerEntry';
import { VisitSiteButton } from './VisitSiteButton';

const useStyles = makeStyles(theme => ({
  card: {
    zIndex: props => props.expanded && 11,
    height: props => (props.isPdf ? '550px' : '100%'),
    minHeight: props => props.isPdf && '550px',
  },
  imageContainer: {
    display: 'block',
    width: '100%',
    height: 123,
    position: 'relative',
    overflow: 'hidden',
  },
  matchedLabel: {
    zIndex: 2,
    position: 'absolute',
    bottom: 12,
    left: 12,
    padding: '4px 8px',
    borderRadius: 12,
    backgroundColor: '#000000B3',
    color: '#FFFFFF',
    ...theme.typography.pxSmall,
  },
  infoWrapper: {
    padding: props => (props.isPdf ? '0 20px 0 20px' : '0 20px'),
    marginTop: 18,
    marginBottom: props => (props.isPdf ? 8 : 18),
    position: 'relative',
    minHeight: 153,
    height: 'calc(100% - 159px)',
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    ...theme.typography.h2,
    paddingRight: 14,
    fontSize: 14,
    lineHeight: '18px',
    cursor: 'pointer',
  },
  iconButton: {
    position: 'absolute',
    top: 0,
    right: 12,
    padding: 0,
    color: theme.palette.text.primary,
    fontSize: 20,
  },
  description: {
    fontSize: 12,
    marginTop: 10,
    lineHeight: '18px',
  },
  highlightsContainer: {
    marginTop: 8,
  },
  itemWrapper: ({ noHover }) => ({
    position: 'relative',
    height: '100%',
    cursor: 'pointer',
    '&:hover': !noHover && {
      backgroundColor: ({ isTopicPage }) =>
        !isTopicPage ? '#F8F8F8' : 'inherit',
    },
    '&:hover + #cta-wrapper': !noHover && {
      backgroundColor: ({ isTopicPage }) =>
        !isTopicPage ? '#F8F8F8' : 'inherit',
    },
  }),
  itemWrapperPointer: {
    cursor: 'pointer',
  },
  spacer: {
    flex: '1',
    minHeight: 16,
  },
  ctaWrapper: ({ clientExclusiveCardStyles, isPdf }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    listStyle: 'none',
    padding: 0,
    justifyContent: 'center',

    ..._get(clientExclusiveCardStyles, 'CtaWrapper', {}),
    '& li': {
      width: isPdf ? 'auto' : 'calc(50% - 2px)',
      minWidth: 106,
      maxWidth: isPdf ? '300px' : '200px',
      ..._get(clientExclusiveCardStyles, 'CtaWrapperLi', {}),
    },
    '& button': {
      width: '100%',
    },
    '&.showMore': {
      justifyContent: 'center',
      '& li': {
        flexGrow: 1,
        maxWidth: '200px',
        ..._get(clientExclusiveCardStyles, 'CtaWrapperLi', {}),
      },
    },
    '&:not(.showMore)': {
      justifyContent: 'center',
      '& li': {
        margin: '0 auto',
        width: isPdf ? 'auto' : undefined,
      },
    },
    [theme.breakpoints.down('md')]: {
      gap: '8px',
    },
  }),
  ctaVerticalLayout: {
    flexDirection: 'column',
    '& li': {
      width: '100%',
      minWidth: 106,
    },
  },
  ctaButton: ({ clientExclusiveCardStyles }) => ({
    borderRadius: 8,
    padding: '5px',
    ...theme.typography.pxSmall,
    textTransform: 'none',
    lineHeight: '18px',
    ..._get(clientExclusiveCardStyles, 'CtaButton', {}),
    '& img, svg ': {
      marginLeft: 5,
    },
  }),
  ctaExpandedTextButton: {
    '& svg': {
      transform: 'rotate(180deg)',
    },
  },
  cardLink: {
    textDecoration: 'none',
  },
  contentContainer: {
    height: '100%',
  },
  img: {
    position: 'absolute',
    top: '50%',
    left: 0,
    width: '100%',
    transform: 'translateY(-50%)',
    filter: props => props.grayscale && 'grayscale(1)',
  },
}));

function LinkItemHybrid({
  item,
  page = 'Home',
  pageShortName,
  isPdf,
  grayscale,
  index,
  eventProperties,
  isPreview = false,
  disableEmbedly,
  componentOverrides,
  alwayShowLinkCardOverlay = false,
}) {
  const clientExclusiveCardStyles = _get(
    componentOverrides,
    'styles.AssessmentResult.ClientRelatedResources.ClientExclusiveCard',
    {},
  );

  const noHover = _get(clientExclusiveCardStyles, 'noHover', false);
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [siteCopy] = useSiteCopySelector(['resource-item-page']);
  const locale = useAlgoliaLocale();
  const clientDetails = useSelector(makeSelectClientDetails());
  const profileFilters = useSelector(state => state.auth.profileFilters);
  const profileTags = _get(profileFilters, 'tags', []);
  const cardRef = useRef();

  const { answersMatchedAudiences, answersMatchedTags } = useContext(
    SurveyResultContext,
  );
  const { shouldABTest, isVariantB } = useContext(AssessmentResultContext);

  const matchedLabels = getMatchedLabels(
    item,
    answersMatchedAudiences,
    answersMatchedTags,
  );
  const accentColor = getAccentColor(clientDetails, '01619B');
  const isTopicPage = page === 'Topic';
  const classes = useStyles({
    isTopicPage,
    expanded,
    grayscale,
    isPdf,
    clientExclusiveCardStyles,
    noHover,
  });
  const history = useHistory();
  const {
    name,
    title,
    type,
    slug,
    exitDisclaimer,
    theme,
    themes,
    topics,
  } = item;

  const entryId = item.objectID || item.sys.id;
  const clientName = clientDetails.name;

  const finalPageName = getEventPageName({
    pageName: page,
    pageShortName,
  });

  const themesTitles = getThemesTitles({ theme, themes });
  const topicsTitles = getTopicsTitles({ topics });

  const themesAndTopics = {
    ...(!_isEmpty(themesTitles) ? { themes: themesTitles } : {}),
    ...(!_isEmpty(topicsTitles) ? { topics: topicsTitles } : {}),
  };

  const skipOverlay = alwayShowLinkCardOverlay
    ? false
    : _get(clientDetails, 'metadata.hybridLinkCardSkipOverlay', false);
  const useVerticalLinkButtonsLayout = _get(
    clientDetails,
    'metadata.useVerticalLinkButtonsLayout',
    false,
  );
  const enableLinkColorOverlay = isPreview
    ? _get(item, 'enableLinkColorOverlay', false)
    : _get(clientDetails, 'metadata.enableLinkColorOverlay', false);
  const linkDisclaimerStyle = _get(item, 'context.linkDisclaimerStyle', {});

  useEffect(() => {
    const listener = window.addEventListener('LinkHybridPopper', () => {
      if (expanded) setExpanded(false);
    });

    return () => window.removeEventListener('LinkHybridPopper', listener);
  }, [expanded]);

  const { isInternal, url } = analyzeClientResource(
    item,
    clientDetails,
    profileTags,
  );

  const showAsLink = isIE() && !isInternal && url;

  const cardTitle =
    getAlgoliaLocalizedField(item, 'title', locale) ||
    getAlgoliaLocalizedField(item, 'name', locale);

  const redirect = isButton => e => {
    if (isButton) {
      e.stopPropagation();
    }
    if (!url && !isInternal) return history.push('/404');

    if (isInternal) {
      Mixpanel.track(
        `Client Resource Card Clicked - ${page}`,
        injectAssessmentResultsABData(
          {
            article: name || title,
            page: finalPageName,
            slug,
            client: clientName,
            entryId,
            title: cardTitle,
            ...themesAndTopics,
            ...(eventProperties || {}),
          },
          shouldABTest,
          isVariantB,
        ),
      );

      history.push(url);
    }
    if (showAsLink) return false;
    if (!isInternal) {
      Mixpanel.track(
        `Client Resource Card Exited - ${page}`,
        injectAssessmentResultsABData(
          {
            page: finalPageName,
            exitPage: url,
            client: clientName,
            entryId,
            target: isButton ? 'button' : 'card',
            title: cardTitle,
            ...themesAndTopics,
            ...(eventProperties || {}),
          },
          shouldABTest,
          isVariantB,
        ),
      );

      const finalUrl = url.includes('http') ? url : `https://${url}`;
      if (skipOverlay || finalUrl.includes('assets.ctfassets.net')) {
        window.open(finalUrl, '_blank', 'noopener');
      } else {
        dispatch(
          setLeavingModal({
            visible: true,
            title:
              getAlgoliaLocalizedField(item, 'title', locale) ||
              getAlgoliaLocalizedField(item, 'name', locale),
            url: finalUrl,
            organization: clientDetails.name,
            resourceType: type || null,
            disclaimer: exitDisclaimer,
            ...themesAndTopics,
          }),
        );
      }
    }
  };

  const file = getImageFile(item, disableEmbedly);

  const cardRootColor = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: `#${accentColor}`,
    opacity: 0.8,
    mixBlendMode: 'screen',
  };

  const cardSubtitle =
    getAlgoliaLocalizedField(item, 'subtitle', locale) ||
    getAlgoliaLocalizedField(item, 'shortDescription', locale);
  const cardDisclaimerText = getAlgoliaLocalizedField(
    item,
    'disclaimerText',
    locale,
  );
  const hasDisclaimerText = !!cardDisclaimerText;
  const hasDescription = !!cardSubtitle;
  const hasHighlights =
    getAlgoliaLocalizedField(item, 'highlights', locale, []).length > 0;
  const showDetailCard = !isPreview && page !== 'User-Resources';

  const handleReadMoreLess = e => {
    e.stopPropagation();

    if (!showDetailCard) {
      const eventInit = expanded
        ? undefined
        : {
            detail: {
              redirect: redirect(true),
              slug,
              index,
              ref: cardRef,
            },
          };
      window.dispatchEvent(new CustomEvent('LinkHybridPopper', eventInit));
    }

    Mixpanel.track(
      `Client Resource Card - Read ${!expanded ? 'More' : 'Less'} Clicked`,
      injectAssessmentResultsABData(
        {
          article: name || title,
          page: finalPageName,
          slug,
          client: clientName,
          entryId,
          title: cardTitle,
          ...themesAndTopics,
          ...(eventProperties || {}),
        },
        shouldABTest,
        isVariantB,
      ),
    );
    setExpanded(!expanded);
  };
  const shouldDisplayVisitSiteButton = !_isEmpty(url);
  const shouldDisplayShowMore =
    !isPdf && (_get(item, 'readMoreInfo') || _get(item, 'nextSteps'));
  const content = (
    <Card
      className={classes.card}
      padding={0}
      borderRadius={_get(clientExclusiveCardStyles, 'BorderRadius', 8)}
      borderColor="#E9ECED"
      mb={isTopicPage ? 20 : 0}
      height="100%"
      fullHeight
      width={isTopicPage ? '100%' : ''}
      id={`item-wrapper-${slug}`}
      ref={cardRef}
    >
      <Box
        onClick={shouldDisplayVisitSiteButton && redirect(false)}
        className={_classNames(classes.itemWrapper, {
          [classes.itemWrapperPointer]: shouldDisplayVisitSiteButton,
        })}
      >
        <Grid container className={classes.contentContainer}>
          <Grid item xs={12} className={classes.imageContainer}>
            {file && (
              <img className={classes.img} src={file} loading="lazy" alt="" />
            )}
            {enableLinkColorOverlay && !isPdf && <div style={cardRootColor} />}
            {!_isEmpty(matchedLabels) && (
              <Typography component="span" className={classes.matchedLabel}>
                {matchedLabels[0]}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} className={classes.infoWrapper}>
            <Typography
              variant="body1"
              color="textPrimary"
              align="left"
              className={classes.title}
              name="title"
            >
              {cardTitle}
            </Typography>
            {hasDescription && (
              <Typography
                color="textPrimary"
                align="left"
                className={classes.description}
                name="description"
              >
                {cardSubtitle}
              </Typography>
            )}
            {hasHighlights && (
              <Box className={classes.highlightsContainer}>
                {getAlgoliaLocalizedField(item, 'highlights', locale, [])
                  .slice(0, 4)
                  .map(highlight => (
                    <HighlightEntry value={highlight} link key={highlight} />
                  ))}
              </Box>
            )}
            {hasDisclaimerText && (
              <Box>
                <DisclaimerEntry
                  value={cardDisclaimerText}
                  componentOverrides={clientExclusiveCardStyles}
                  linkDisclaimerStyle={linkDisclaimerStyle}
                />
              </Box>
            )}
            <Box className={classes.spacer} />
            <ul
              className={_classNames(
                classes.ctaWrapper,
                useVerticalLinkButtonsLayout && classes.ctaVerticalLayout,
                shouldDisplayShowMore && 'showMore',
              )}
            >
              {clientExclusiveCardStyles ? (
                <>
                  {shouldDisplayVisitSiteButton && (
                    <li>
                      <VisitSiteButton
                        title={cardTitle}
                        siteCopy={siteCopy}
                        onClick={redirect(true)}
                        componentOverrides={clientExclusiveCardStyles}
                      />
                    </li>
                  )}
                  {shouldDisplayShowMore && (
                    <li>
                      <Button
                        variant="outlined"
                        className={_classNames(classes.ctaButton, {
                          [classes.ctaExpandedTextButton]: expanded,
                        })}
                        onClick={handleReadMoreLess}
                        color="primary"
                        aria-label={`${cardTitle} ${
                          expanded ? 'Read Less' : 'Read More'
                        }`}
                      >
                        {toSentenceCase(
                          _get(
                            siteCopy,
                            expanded
                              ? 'pageCopy.hybridLinkCardReadLess'
                              : 'pageCopy.hybridLinkCardReadMore',
                            expanded ? 'Read Less' : 'Read More',
                          ),
                        )}
                        <KeyboardArrowDownIcon
                          color="primary"
                          fontSize="small"
                        />
                      </Button>
                    </li>
                  )}
                </>
              ) : (
                <>
                  {shouldDisplayShowMore && (
                    <li>
                      <Button
                        variant="outlined"
                        className={_classNames(classes.ctaButton, {
                          [classes.ctaExpandedTextButton]: expanded,
                        })}
                        onClick={handleReadMoreLess}
                        color="primary"
                        aria-label={`${cardTitle} ${
                          expanded ? 'Read Less' : 'Read More'
                        }`}
                      >
                        {toSentenceCase(
                          _get(
                            siteCopy,
                            expanded
                              ? 'pageCopy.hybridLinkCardReadLess'
                              : 'pageCopy.hybridLinkCardReadMore',
                            expanded ? 'Read Less' : 'Read More',
                          ),
                        )}
                        <KeyboardArrowDownIcon
                          color="primary"
                          fontSize="small"
                        />
                      </Button>
                    </li>
                  )}
                  {shouldDisplayVisitSiteButton && (
                    <li>
                      <VisitSiteButton
                        title={cardTitle}
                        siteCopy={siteCopy}
                        onClick={redirect(true)}
                        componentOverrides={clientExclusiveCardStyles}
                      />
                    </li>
                  )}
                </>
              )}
            </ul>
          </Grid>
        </Grid>
      </Box>
      {expanded && showDetailCard && (
        <LinkDetailCard
          item={item}
          onClose={() => setExpanded(false)}
          onClick={redirect(true)}
          componentOverrides={clientExclusiveCardStyles}
          shouldDisplayVisitSiteButton={shouldDisplayVisitSiteButton}
        />
      )}
    </Card>
  );

  if (showAsLink || isPdf)
    return (
      <a
        href={url}
        target="_blank"
        aria-label={`Open ${cardTitle} in a new tab`}
        className={classes.cardLink}
      >
        {content}
      </a>
    );

  return content;
}

LinkItemHybrid.propTypes = {
  item: PropTypes.object,
  clientDetails: PropTypes.object,
  page: PropTypes.string,
  isBiggerCard: PropTypes.bool,
  isPdf: PropTypes.bool,
  grayscale: PropTypes.bool,
  eventProperties: PropTypes.object,
};

export default React.memo(LinkItemHybrid);
